import { orderedFeatures, orderedStates } from './order.js';
/**
 * A world is the place, not the lesson (ADR-097; decided by Pieter 2 Nov 2026, after he withdrew six
 * worlds that were lessons: a giant eye whose states walked through how it sees, the retina's rods and
 * cones, a grid of molecules, a race of sperm cells to the egg, an eruption told in five steps and a
 * volcano's cross-section with its names to switch on). One place for the test, the word lists and
 * the checks, so `wereld check`, the platform's upload, the console's approval and the docs say the
 * same. Like the design rules (design-rules.ts), applied to a package when it is uploaded, never to
 * one that is served: published worlds keep working, and the Regelcheck does not read these rules.
 *
 * What it refuses is a clear sign (text in the world, names to switch on, a lesson file in the
 * package); what it only warns of is a signal a person judges when approving the world.
 */
/** The test, as a maker, a task and an approver read it (Dutch). */
export const WORLD_NOT_LESSON_TEST = 'Een wereld is de plek, niet de les. Hij past bij minstens drie verschillende lessen. Er staat geen tekst in, geen uitleg en geen vragen, en hij heeft geen vaste volgorde van stappen. Wat de leerling van dichtbij onderzoekt (een oog, een cel, een molecuul) is een lesobject: een model uit de bibliotheek, geen wereld. Wat je over de wereld weet, hoort in het feitenblad van de wereld.';
/** The question the console asks before a world is approved. */
export const WORLD_NOT_LESSON_QUESTION = 'Past deze wereld bij meerdere lessen?';
/**
 * Words that name text in the world (refused): a mesh, block or feature called so shows words a
 * lesson should say. Matched as a whole word, as the end of a compound ("straatnaam", "naamlabel";
 * not for the English text, name and title), or, for naam, tekst and label, as its start ("naambord",
 * "tekstvak").
 */
export const TEXT_WORDS = [
    'tekst',
    'teksten',
    'letter',
    'letters',
    'cijfer',
    'cijfers',
    'label',
    'labels',
    'naam',
    'namen',
    'opschrift',
    'opschriften',
    'bijschrift',
    'bijschriften',
    'onderschrift',
    'legenda',
    'titel',
    'titels',
    'text',
    'texts',
    'caption',
    'captions',
    'lettering',
    'name',
    'names',
    'title',
];
const TEXT_PREFIXES = ['naam', 'tekst', 'label'];
/** The English words are matched whole only: "context" is no text, "rename" no name. */
const WHOLE_ONLY = new Set(['text', 'texts', 'name', 'names', 'title']);
/**
 * Words for a pointer (refused, as text is; Pieter, 2 Nov 2026: "pointers belong in the lesson, never
 * in the world"): a mesh, block or onderdeel called so points something out. Matched whole, or as a
 * compound's end for the Dutch ones ("richtingpijl"); the English ones whole only. Not "wijzer": a
 * clock's hands (the practicumlokaal's "klok-wijzer") are scenery.
 */
export const POINTER_WORDS = [
    'aanwijzer',
    'aanwijzers',
    'wegwijzer',
    'wegwijzers',
    'pijl',
    'pijlen',
    'pijltje',
    'pijltjes',
    'pointer',
    'pointers',
    'arrow',
    'arrows',
];
const POINTER_WHOLE_ONLY = new Set(['pointer', 'pointers', 'arrow', 'arrows']);
/**
 * Words for a board (only warned of; Pieter, 2 Nov 2026): a board can be scenery (a whiteboard, a
 * water gauge) or carry names. A block an onderdeel switches, or a GLB's mesh, called so is what a
 * person should look at; an onderdeel that only switches several boards, or whose states say names
 * ("alle", "zonder", "geen"), is a names feature and refused (`textProblems`).
 */
export const SIGN_WORDS = [
    'bord',
    'borden',
    'bordje',
    'bordjes',
    'board',
    'sign',
    'signs',
];
/** State words that switch labels as a set: with boards only, the onderdeel switches names. */
const LABEL_SET_WORDS = ['alle', 'zonder', 'geen'];
/** Words of a lesson's steps, in an onderdeel's key or name or a state's key (warned of). */
export const STEP_WORDS = [
    'stap',
    'stappen',
    'vraag',
    'vragen',
    'antwoord',
    'antwoorden',
    'uitleg',
    'opdracht',
    'opdrachten',
    'quiz',
    'oefening',
    'toets',
    'les',
    'lessen',
    'step',
    'steps',
    'question',
    'questions',
    'answer',
    'lesson',
];
/** Words that put states in a story's order ("na-de-uitbarsting", "zojuist-bevrucht"; warned of). */
export const STORY_WORDS = [
    'na',
    'nadat',
    'daarna',
    'zojuist',
    'eerst',
    'vervolgens',
    'tenslotte',
    'uiteindelijk',
    'later',
    'voordat',
    'begin',
    'einde',
    'eind',
    'afloop',
    'eerste',
    'tweede',
    'derde',
    'vierde',
    'vijfde',
    'laatste',
];
/**
 * What a pupil examines close up (warned of when the world is named so, or an onderdeel): a lesson
 * object, which is a model in the library. Matched as a whole word, or as the end of a compound for
 * the longer ones ("eicel", "zaadcellen", "reuzenmoleculen").
 */
export const CLOSE_UP_WORDS = [
    'oog',
    'ogen',
    'eye',
    'eyes',
    'netvlies',
    'retina',
    'hoornvlies',
    'cornea',
    'staafjes',
    'kegeltjes',
    'hersenen',
    'brein',
    'brain',
    'hart',
    'heart',
    'longen',
    'lung',
    'lungs',
    'nier',
    'nieren',
    'kidney',
    'maag',
    'stomach',
    'darm',
    'darmen',
    'orgaan',
    'organen',
    'organ',
    'organs',
    'cel',
    'cellen',
    'cell',
    'cells',
    'molecuul',
    'moleculen',
    'molecule',
    'molecules',
    'atoom',
    'atomen',
    'atom',
    'atoms',
    'dna',
    'chromosoom',
    'chromosomen',
    'skelet',
    'skeleton',
    'schedel',
    'skull',
    'doorsnede',
    'dwarsdoorsnede',
];
/** Close-up words a compound may end in (with at least two letters before them). */
const CLOSE_UP_ENDINGS = ['cel', 'cellen', 'molecuul', 'moleculen', 'atoom', 'atomen', 'doorsnede'];
/** At least this many states of one onderdeel, each showing more than the one before, read as steps. */
export const STEP_CHAIN = 5;
/** At least this many states of one onderdeel numbered ("poort-1", "poort-2", ...) read as steps. */
export const NUMBERED_STATES = 3;
/**
 * A description that teaches rather than describes the place: a question, the pupil told what to
 * do, or an explanation (what something does, why). Matched in the Dutch description.
 */
const QUESTION = /\?/;
const INSTRUCTION = /(?:^|[.!:;]\s+)(kijk|ontdek|zoek|probeer|vergelijk|bedenk|tel|onderzoek|leer|volg|wijs|beantwoord|noem)\b|\b(je leert|leer je|ontdek je|zie je hoe|je ziet hoe|laat zien hoe|let op)\b/iu;
const EXPLANATION = /\b(omdat|doordat|waardoor|zodat|daardoor|daarom|hierdoor|want|zorgt|zorgen|helpt|helpen|veroorzaakt|leidt tot|beschermt|beschermen|vervoert|vervoeren|dient|dienen|(?:werkt|werken)\s+(?:in|als|met|door|bij|zo|samen|op)|betekent|noemen we|noem je|wordt\s+\S+\s+genoemd|heet het|dit heet|heten ze)\b/iu;
/** A name's words: lower case, accents off, split on anything but letters. */
function wordsOf(text) {
    return text
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .split(/[^a-z]+/)
        .filter((word) => word !== '');
}
/** The text word a name has (`TEXT_WORDS`), or undefined. */
export function textWordOf(name) {
    for (const word of wordsOf(name)) {
        const found = TEXT_WORDS.find((text) => word === text ||
            (!WHOLE_ONLY.has(text) &&
                text.length >= 4 &&
                word.length - text.length >= 3 &&
                word.endsWith(text)) ||
            (TEXT_PREFIXES.includes(text) &&
                word.length - text.length >= 3 &&
                word.startsWith(text)));
        if (found !== undefined)
            return word;
    }
    return undefined;
}
/**
 * A railing's bar (spijl), not a pointer: "spijl" alone or after one of these words. A pointer with a linking
 * s ("richtingspijl") is still read as one.
 */
const BALUSTER_STEMS = ['', 'hek', 'tralie', 'trap', 'balustrade', 'leuning', 'raam', 'brug'];
const isBaluster = (word) => BALUSTER_STEMS.some((stem) => word === `${stem}spijl` || word === `${stem}spijlen`);
/** The pointer word a name has (`POINTER_WORDS`), or undefined. */
export function pointerWordOf(name) {
    return wordsOf(name).find((word) => !isBaluster(word) &&
        POINTER_WORDS.some((pointer) => word === pointer ||
            (!POINTER_WHOLE_ONLY.has(pointer) &&
                word.length - pointer.length >= 3 &&
                word.endsWith(pointer))));
}
/** What a name says that a world may not have: text (`TEXT_WORDS`) or a pointer (`POINTER_WORDS`). */
function forbiddenOf(name) {
    const text = textWordOf(name);
    if (text !== undefined)
        return { word: text, kind: 'tekst' };
    const pointer = pointerWordOf(name);
    return pointer === undefined ? undefined : { word: pointer, kind: 'aanwijzer' };
}
/**
 * The sign word a name has (`SIGN_WORDS`), or undefined. A Dutch board also as a compound's end ("scorebord");
 * the English words only whole, so a skateboard, a dashboard or "designs" is no sign.
 */
export function signWordOf(name) {
    return wordsOf(name).find((word) => SIGN_WORDS.some((sign) => word === sign ||
        (sign.startsWith('bord') && word.length - sign.length >= 3 && word.endsWith(sign))));
}
/** The close-up word a name has (`CLOSE_UP_WORDS`), or undefined. */
export function closeUpWordOf(name) {
    const words = wordsOf(name);
    // "cross-section" is two words.
    for (let i = 0; i + 1 < words.length; i++) {
        if (words[i] === 'cross' && words[i + 1] === 'section')
            return 'cross-section';
    }
    return words.find((word) => CLOSE_UP_WORDS.includes(word) ||
        CLOSE_UP_ENDINGS.some((end) => word.length - end.length >= 2 && word.endsWith(end)));
}
const has = (list, name) => wordsOf(name).find((word) => list.includes(word));
/** A block's names: its id and the meshes it names (`parts`, `part`, `mesh`). */
function blockNames(block) {
    const raw = block;
    return [
        raw.id,
        ...(Array.isArray(raw.parts)
            ? raw.parts.filter((p) => typeof p === 'string')
            : []),
        ...(typeof raw.part === 'string' ? [raw.part] : []),
        ...(typeof raw.mesh === 'string' ? [raw.mesh] : []),
    ];
}
/** The blocks each onderdeel changes, by id ("<blockId>.<setting>" in any of its states). */
function changedBlocks(pkg, feature) {
    const ids = new Set();
    for (const changes of Object.values(pkg.features[feature].states)) {
        for (const key of Object.keys(changes))
            ids.add(key.slice(0, key.indexOf('.')));
    }
    return ids;
}
/** What a state turns on: its settings that show something (true, or a number above 0). */
function shown(changes) {
    return new Set(Object.entries(changes)
        .filter(([, value]) => value === true || (typeof value === 'number' && value > 0))
        .map(([key]) => key));
}
const quote = (names) => names.map((n) => `"${n}"`).join(', ');
/** "a", "a en b", "a, b en c". */
const list = (parts) => parts.length <= 1 ? (parts[0] ?? '') : `${parts.slice(0, -1).join(', ')} en ${parts.at(-1)}`;
const LESSON_FILE_WORDS = [
    'les',
    'lessen',
    'lesson',
    'lessons',
    'stappen',
    'steps',
    'vragen',
    'questions',
    'quiz',
    'opdracht',
    'script',
];
/**
 * A lesson in the package (refused): any JSON file but wereld.json, or a text file named as a lesson
 * ("les.md", "vragen.txt"). A lesson is made with Les maken or the lesson generator, never shipped
 * in a world.
 */
export function lessonFileProblems(names) {
    return names
        .filter((name) => {
        const lower = name.toLowerCase();
        if (lower === 'wereld.json')
            return false;
        if (lower.endsWith('.json'))
            return true;
        if (!/\.(md|txt|yaml|yml|csv)$/.test(lower))
            return false;
        return wordsOf(lower.replace(/\.[a-z]+$/, '')).some((w) => LESSON_FILE_WORDS.includes(w));
    })
        .map((name) => ({
        path: `files/${name}`,
        message: `is een les of hoort bij een les: een wereld is de plek, niet de les. Haal het uit het pakket; een les maak je met Les maken of de lesgenerator, en wat je over de wereld weet, hoort in het feitenblad`,
    }));
}
/** The refusal's reason, as a maker reads it. */
const NO_TEXT = 'een wereld is de plek, niet de les, en heeft geen tekst, namen, labels of aanwijzers. Wat iets heet en waar je naar kijkt, zegt de les; wat je over de wereld weet, hoort in het feitenblad';
const named = (found) => `${found.kind === 'tekst' ? 'naar tekst' : 'naar een aanwijzer'} ("${found.word}")`;
/** The boards of a package: block id to its sign word (`SIGN_WORDS`). */
function signBlocksOf(pkg) {
    const signs = new Map();
    for (const block of pkg.blocks) {
        const word = blockNames(block)
            .map(signWordOf)
            .find((w) => w !== undefined);
        if (word !== undefined)
            signs.set(block.id, word);
    }
    return signs;
}
/**
 * The clear signs (refused): a block, a mesh it names or an onderdeel named as text or a pointer
 * (`TEXT_WORDS`, `POINTER_WORDS`); an onderdeel whose states switch such a block; an onderdeel that
 * switches only boards, several of them or by states that say names ("alle", "zonder-namen": a
 * names feature by another word). Given what the worker measured, also the GLBs' own mesh names.
 */
export function textProblems(pkg, measured) {
    const problems = [];
    const forbidden = new Set();
    pkg.blocks.forEach((block, index) => {
        const found = blockNames(block)
            .map(forbiddenOf)
            .find((f) => f !== undefined);
        if (found === undefined)
            return;
        forbidden.add(block.id);
        problems.push({
            path: `/blocks/${index}`,
            message: `het blok "${block.id}" heet ${named(found)}: ${NO_TEXT}`,
        });
    });
    const boards = signBlocksOf(pkg);
    for (const feature of orderedFeatures(pkg)) {
        const { name, states } = pkg.features[feature];
        const own = [feature, name, ...Object.keys(states)].map(forbiddenOf).find((f) => f);
        const changed = [...changedBlocks(pkg, feature)];
        const switched = changed.filter((id) => forbidden.has(id));
        const onlyBoards = changed.length > 0 && changed.every((id) => boards.has(id));
        const labelSet = Object.keys(states).some((state) => has(LABEL_SET_WORDS, state) !== undefined);
        const names = onlyBoards && (changed.length >= 2 || labelSet);
        if (own === undefined && switched.length === 0 && !names)
            continue;
        problems.push({
            path: `/features/${feature}`,
            message: switched.length > 0
                ? `het onderdeel "${name}" zet tekst of aanwijzers aan en uit (${quote(switched)}): wat je aanzet om iets te benoemen of aan te wijzen, is een les. Haal het uit de wereld`
                : own !== undefined
                    ? `het onderdeel "${name}" heet ${named(own)}: ${NO_TEXT}`
                    : `het onderdeel "${name}" zet alleen borden aan en uit (${quote(changed)}): dat zijn namen bij een ander woord, en namen die je aanzet, zijn een les. Haal ze uit de wereld`,
        });
    }
    if (measured) {
        for (const [file, glb] of Object.entries(measured.glbs)) {
            const hits = [...new Set(glb.meshes.map((mesh) => mesh.name))].filter((n) => forbiddenOf(n) !== undefined);
            if (hits.length === 0)
                continue;
            problems.push({
                path: `files/${file}`,
                message: `heeft ${hits.length === 1 ? 'een mesh die' : 'meshes die'} naar tekst of een aanwijzer ${hits.length === 1 ? 'heet' : 'heten'} (${quote(hits.slice(0, 5))}${hits.length > 5 ? ', …' : ''}): ${NO_TEXT}. Haal ze uit de wereld, of geef de mesh een naam naar wat hij is`,
            });
        }
    }
    return problems;
}
/**
 * The signals (warned of; the approver judges): an onderdeel that switches a board, and, given what
 * the worker measured, a GLB's mesh named as a board that an onderdeel switches; states that read as a lesson's steps (step
 * words, numbered states, a story's order, `STEP_CHAIN` states that each show more); a description
 * that teaches; and a world about what a pupil examines close up.
 */
export function lessonWarnings(pkg, measured) {
    const warnings = [];
    const signBlocks = signBlocksOf(pkg);
    // A sign is said once, with every onderdeel that switches it.
    const signs = new Map();
    const signAt = warnings.length;
    for (const feature of orderedFeatures(pkg)) {
        const { name, states } = pkg.features[feature];
        const order = orderedStates(pkg, feature);
        const path = `/features/${feature}`;
        for (const id of changedBlocks(pkg, feature)) {
            if (signBlocks.has(id))
                signs.set(id, [...(signs.get(id) ?? []), name]);
        }
        const step = [feature, name].map((n) => has(STEP_WORDS, n)).find((w) => w);
        const stepStates = order.filter((state) => has(STEP_WORDS, state) !== undefined);
        const numbered = order.filter((state) => /\d$/.test(state));
        const story = order.filter((state) => has(STORY_WORDS, state) !== undefined);
        let chain = 1;
        let longest = 1;
        for (let i = 1; i < order.length; i++) {
            const before = shown(states[order[i - 1]]);
            const now = shown(states[order[i]]);
            const grows = now.size > before.size && [...before].every((key) => now.has(key));
            chain = grows ? chain + 1 : 1;
            longest = Math.max(longest, chain);
        }
        const why = [];
        if (step !== undefined)
            why.push(`het onderdeel heet naar een lesstap ("${step}")`);
        if (stepStates.length > 0)
            why.push(stepStates.length === 1
                ? `de toestand ${quote(stepStates)} is een lesstap`
                : `de toestanden ${quote(stepStates)} zijn lesstappen`);
        if (numbered.length >= NUMBERED_STATES)
            why.push(`de toestanden ${quote(numbered)} zijn genummerd`);
        if (story.length > 0)
            why.push(story.length === 1
                ? `de toestand ${quote(story)} vertelt een volgorde`
                : `de toestanden ${quote(story)} vertellen een volgorde`);
        if (longest >= STEP_CHAIN)
            why.push(`${longest} toestanden na elkaar laten elk meer zien dan de vorige`);
        if (why.length > 0) {
            warnings.push({
                path,
                message: `de toestanden van "${name}" lezen als de stappen van een les: ${why.join('; ')}. Een wereld heeft geen vaste volgorde van stappen; geef elke toestand een naam naar hoe de plek er dan bij ligt, of maak er een les van`,
            });
        }
    }
    if (signs.size > 0) {
        const features = [...new Set([...signs.values()].flat())];
        const first = orderedFeatures(pkg).find((f) => features.includes(pkg.features[f].name));
        warnings.splice(signAt, 0, {
            path: `/features/${first}`,
            message: `${features.length === 1 ? 'het onderdeel' : 'de onderdelen'} ${quote(features)} ${features.length === 1 ? 'zet' : 'zetten'} een bord aan en uit (${quote([...signs.keys()])}): staat er een naam of uitleg op, dan is het een label, en een label is een les. Een wereld is de plek, niet de les`,
        });
    }
    if (measured) {
        // Only a board an onderdeel switches (a mesh under a part of a block it changes): a board that
        // just stands there is scenery (the polder's water gauge, the practicumlokaal's whiteboard).
        const switchedParts = orderedFeatures(pkg).flatMap((feature) => {
            const changed = changedBlocks(pkg, feature);
            return pkg.blocks.filter((block) => changed.has(block.id)).flatMap(blockNames);
        });
        for (const [file, glb] of Object.entries(measured.glbs)) {
            const boards = [...new Set(glb.meshes.map((mesh) => mesh.name))].filter((n) => signWordOf(n) !== undefined &&
                forbiddenOf(n) === undefined &&
                switchedParts.some((part) => part !== '' && n.startsWith(part)));
            if (boards.length === 0)
                continue;
            warnings.push({
                path: `files/${file}`,
                message: `heeft ${boards.length === 1 ? 'een mesh die' : 'meshes die'} naar een bord ${boards.length === 1 ? 'heet' : 'heten'} en die een onderdeel aan- en uitzet (${quote(boards.slice(0, 5))}${boards.length > 5 ? ', …' : ''}): staat er een naam of uitleg op, dan is het een label, en een label is een les. Een bord als decor (een schoolbord, een peilschaal) mag`,
            });
        }
    }
    const description = pkg.description ?? '';
    const teaches = [
        QUESTION.test(description) ? 'stelt een vraag' : undefined,
        INSTRUCTION.exec(description) ? 'vertelt de leerling wat hij moet doen' : undefined,
        EXPLANATION.exec(description) ? 'legt uit hoe iets werkt' : undefined,
    ].filter((w) => w !== undefined);
    if (teaches.length > 0) {
        const word = (INSTRUCTION.exec(description) ?? EXPLANATION.exec(description))?.find((part, i) => i > 0 && part !== undefined);
        warnings.push({
            path: '/description',
            message: `de beschrijving ${list(teaches)}${word ? ` ("${word}")` : ''}: beschrijf de plek (wat de leerling ziet en waar), niet de les. Wat je over de wereld weet, hoort in het feitenblad van de wereld`,
        });
    }
    const closeUp = [pkg.name.nl, pkg.name.en, pkg.slug]
        .map((n) => closeUpWordOf(n))
        .find((w) => w !== undefined);
    const closeFeatures = orderedFeatures(pkg).filter((feature) => closeUpWordOf(feature) !== undefined ||
        closeUpWordOf(pkg.features[feature].name) !== undefined);
    if (closeUp !== undefined || closeFeatures.length > 0) {
        const what = closeUp !== undefined
            ? `de wereld heet naar "${closeUp}"`
            : `${closeFeatures.length === 1 ? 'het onderdeel' : 'de onderdelen'} ${quote(closeFeatures.map((f) => pkg.features[f].name))} ${closeFeatures.length === 1 ? 'is' : 'zijn'} iets dat de leerling van dichtbij onderzoekt`;
        warnings.push({
            path: closeUp !== undefined ? '/name' : `/features/${closeFeatures[0]}`,
            message: `${what}: dat is een lesobject (een model uit de bibliotheek), geen wereld. Een wereld is de plek waar zo'n les zich afspeelt, en past bij minstens drie verschillende lessen`,
        });
    }
    return warnings;
}
//# sourceMappingURL=not-a-lesson.js.map